import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BOOKS } from "@/data/liberacionReino";
import { useApp } from "@/context/AppContext";
import { BookOpen, ChevronLeft, ChevronRight, List, X, ArrowLeft } from "lucide-react";

// Pick localized value from { es, en } or return raw string
const L = (v, lang) => (v && typeof v === "object" ? v[lang] || v.es || v.en || "" : v || "");

function ChapterBody({ blocks, lang, labels }) {
  return (
    <div className="space-y-5 font-serif-body leading-relaxed text-[var(--text-primary)]">
      {blocks.map((b, i) => {
        if (b.type === "h3") {
          return (
            <h3
              key={i}
              className="font-serif text-xl md:text-2xl tracking-tight mt-8 text-[var(--text-primary)]"
              data-testid={`book-h3-${i}`}
            >
              {L(b.text, lang)}
            </h3>
          );
        }
        if (b.type === "quote") {
          return (
            <blockquote
              key={i}
              className="relative pl-5 border-l-2 border-[var(--accent)] py-1 my-2"
              data-testid={`book-quote-${i}`}
            >
              <p className="italic text-[var(--text-primary)] leading-relaxed">
                “{L(b.text, lang)}”
              </p>
              {b.ref && (
                <p className="uppercase tracking-[0.2em] text-[10px] text-[var(--accent)] mt-2">
                  {L(b.ref, lang)}
                </p>
              )}
            </blockquote>
          );
        }
        if (b.type === "note") {
          return (
            <div
              key={i}
              className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4"
              data-testid={`book-note-${i}`}
            >
              <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mb-1">
                {labels.note}
              </p>
              <p className="text-[var(--text-primary)]">{L(b.text, lang)}</p>
            </div>
          );
        }
        if (b.type === "highlight") {
          return (
            <div
              key={i}
              className="rounded-2xl border border-[var(--accent)]/40 bg-[var(--accent)]/5 p-5"
              data-testid={`book-highlight-${i}`}
            >
              <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)] mb-2">
                {labels.keyThought}
              </p>
              <p className="text-[var(--text-primary)] font-serif-body italic">
                {L(b.text, lang)}
              </p>
            </div>
          );
        }
        return (
          <p key={i} className="text-[var(--text-primary)]" data-testid={`book-p-${i}`}>
            {L(b.text, lang)}
          </p>
        );
      })}
    </div>
  );
}

export default function BookReader() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { lang } = useApp();
  const activeLang = lang === "es" ? "es" : "en";
  const book = BOOKS[bookId];

  const storageKey = book ? `book_progress_${book.id}` : null;

  const initialIndex = useMemo(() => {
    if (!storageKey) return 0;
    const saved = parseInt(localStorage.getItem(storageKey) || "0", 10);
    return Number.isFinite(saved) ? saved : 0;
  }, [storageKey]);

  const [index, setIndex] = useState(initialIndex);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, String(index));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [index, storageKey]);

  const T = (en, es) => (activeLang === "es" ? es : en);
  const labels = {
    back: T("Studies", "Estudios"),
    toc: T("Index", "Índice"),
    prev: T("Previous", "Anterior"),
    next: T("Next", "Siguiente"),
    note: T("Note", "Nota"),
    keyThought: T("Key thought", "Frase central"),
    reading: T("reading", "leyendo"),
    notFound: T("Book not found.", "Libro no encontrado."),
    backToStudies: T("← Back to Studies", "← Volver a Estudios"),
    powerCategory: T("Power & warfare", "Poder y guerra"),
  };

  if (!book) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <p className="text-[var(--text-secondary)]">{labels.notFound}</p>
        <button
          onClick={() => navigate("/studies")}
          className="mt-6 text-[var(--accent)] uppercase tracking-widest text-sm"
        >
          {labels.backToStudies}
        </button>
      </div>
    );
  }

  const chapter = book.chapters[index];
  const total = book.chapters.length;
  const progressPct = Math.round(((index + 1) / total) * 100);

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(total - 1, i + 1));

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 pb-24">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => navigate("/studies")}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          data-testid="book-back-studies"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span>{labels.back}</span>
        </button>
        <button
          onClick={() => setTocOpen(true)}
          className="flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-1.5 text-xs uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          data-testid="book-open-toc"
        >
          <List className="w-3.5 h-3.5" strokeWidth={1.5} />
          {labels.toc}
        </button>
      </div>

      {/* Book header */}
      <div className="mt-6 mb-8">
        <div className="flex items-center gap-2 text-[var(--accent)]">
          <BookOpen className="w-4 h-4" strokeWidth={1.5} />
          <span className="uppercase tracking-[0.3em] text-[10px]">
            {book.attribution} · {labels.powerCategory}
          </span>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">
          {L(book.title, activeLang)}
        </h1>
        {book.subtitle && (
          <p className="mt-2 text-[var(--text-secondary)] text-sm">
            {L(book.subtitle, activeLang)}
          </p>
        )}

        {/* Progress bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-[var(--text-secondary)]">
            <span>{L(chapter.label, activeLang)}</span>
            <span>{index + 1} / {total}</span>
          </div>
          <div className="mt-2 h-[3px] w-full bg-[var(--surface)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--accent)] transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Chapter content */}
      <article className="mt-2" data-testid="book-chapter">
        <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-6">
          {L(chapter.title, activeLang)}
        </h2>
        <ChapterBody blocks={chapter.blocks} lang={activeLang} labels={labels} />
      </article>

      {/* Chapter nav */}
      <div className="mt-12 pt-6 border-t border-[var(--border)] flex items-center justify-between gap-3">
        <button
          onClick={goPrev}
          disabled={index === 0}
          className="flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface-elevated)] px-4 py-2 text-sm text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--surface)] transition-colors"
          data-testid="book-prev-chapter"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          <span>{labels.prev}</span>
        </button>
        <button
          onClick={goNext}
          disabled={index >= total - 1}
          className="flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface-elevated)] px-4 py-2 text-sm text-[var(--text-primary)] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--surface)] transition-colors"
          data-testid="book-next-chapter"
        >
          <span>{labels.next}</span>
          <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* TOC drawer */}
      {tocOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
          onClick={() => setTocOpen(false)}
        >
          <div
            className="w-[85%] max-w-sm h-full overflow-y-auto bg-[var(--surface-elevated)] border-l border-[var(--border)] p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="book-toc-drawer"
          >
            <div className="flex items-center justify-between">
              <p className="uppercase tracking-[0.3em] text-[10px] text-[var(--text-secondary)]">
                {labels.toc}
              </p>
              <button
                onClick={() => setTocOpen(false)}
                className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                data-testid="book-close-toc"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <h3 className="font-serif text-xl tracking-tight mt-3 mb-6">
              {L(book.title, activeLang)}
            </h3>
            <ul className="space-y-1">
              {book.chapters.map((c, i) => (
                <li key={c.id}>
                  <button
                    onClick={() => {
                      setIndex(i);
                      setTocOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors ${
                      i === index
                        ? "bg-[var(--accent)]/10 text-[var(--text-primary)]"
                        : "hover:bg-[var(--surface)] text-[var(--text-primary)]"
                    }`}
                    data-testid={`book-toc-item-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="uppercase tracking-[0.2em] text-[10px] text-[var(--text-secondary)]">
                        {L(c.label, activeLang)}
                      </span>
                      {i === index && (
                        <span className="uppercase tracking-[0.2em] text-[10px] text-[var(--accent)]">
                          {labels.reading}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 font-serif text-base leading-snug">
                      {L(c.title, activeLang)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
